import {Queries} from '../../api/queries';
import {Select, type TypeSelectProps} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {SyntheticEvent, useCallback, useId} from 'react';

type ProductPickerProps = Omit<
	TypeSelectProps,
	'id' | 'name' | 'value' | 'onChange' | 'children'
> & {
	id?: string;
	name?: string;
	selectedProductId: string | null;
	selectedProductVariantId: string | null;
	onChange: (
		event: SyntheticEvent<HTMLSelectElement>,
		productId: string | null,
		productVariantId: string | null,
	) => void;
};

const EMPTY_VALUE = 'null';

const ProductPicker = ({
	selectedProductId,
	selectedProductVariantId,
	onChange,
	...rest
}: ProductPickerProps) => {
	const id = useId();

	const {data: products} = useQuery(Queries.products());

	const onValueSelected = useCallback(
		(event: SyntheticEvent<HTMLSelectElement>) => {
			if (event.currentTarget.value === EMPTY_VALUE) {
				onChange(event, null, null);
			} else {
				const [productId, variantId] = parseCompositeProductId(event.currentTarget.value);

				onChange(event, productId, variantId ?? null);
			}
		},
		[onChange],
	);

	return (
		<Select
			{...rest}
			id={rest.id ?? id}
			name={rest.name ?? id}
			value={
				selectedProductId
					? buildCompositeProductId(selectedProductId, selectedProductVariantId)
					: EMPTY_VALUE
			}
			onChange={onValueSelected}
		>
			<option value={EMPTY_VALUE}>(None)</option>
			{products?.map((product) => {
				if (!product.variants?.length) {
					return <option key={product.id} value={product.id + ''} children={product.name} />;
				} else {
					return (
						<optgroup key={'group:' + product.id} label={product.name}>
							<option value={product.id + ''} children={product.name} />
							{product.variants.map((variant) => (
								<option
									key={buildCompositeProductId(product.id, variant.id)}
									value={buildCompositeProductId(product.id, variant.id)}
									children={`${product.name} - ${variant.description}`}
								/>
							))}
						</optgroup>
					);
				}
			})}
		</Select>
	);
};

type CompositeProductId = string;

const buildCompositeProductId = (
	productId: string,
	variantId: string | null,
): CompositeProductId => {
	return [productId, variantId].filter(Boolean).join(':');
};

const parseCompositeProductId = (id: CompositeProductId): [string, string | null] => {
	const [productId, variantId] = id.split(':');

	return [productId, variantId ?? null];
};

export default ProductPicker;
